import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
    PathwayCenteredTableCellContent,
    PathwayCenteredTableHeader,
    PathwayTable,
    PathwayTableCellContent,
    PathwayTableContentRow,
    PathwayTableHeader,
    PathwayTableHeaderRow,
    TableContentDividerLine,
} from "../../../_UI/StyledComponents/PathwayTable";
import {
    PathwayAddButton,
    PathwayButton,
    PathwayDeleteButton,
    PathwayEditButton,
} from "../../../_UI/Components/PathwayButtons";
import { Paragraph } from "../../../_UI/StyledComponents/StyledComponents";
import stringUtils from "../../../_services/string-utils";
import { WarningMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import { getDatevConnections } from "../services/clients.actions";
import DatevConnectionButton from "../../../_components/DatevConnectionButton";
import dateUtils from "../../../_services/date-utils";
import LoadingSpinner from "../../../_UI/Components/LoadingSpinner";
import { AuthDataType } from "../../../_statics/AuthDataType";
import { AuthDataStatus } from "../../../_statics/AuthDataStatus";
import { UserRole } from "../../../_statics/UserStatics";
import { getEnvironment, EnvironmentEnum } from "../../../EnvironmentUtils";
import ClientAutoUpdateFromProdModal from "./ClientAutoUpdateFromProdModal";

const ClientsScreenClientsTabContent = (props) => {
    const dispatch = useDispatch();
    const clientReducer = useSelector((state) => state.client);
    const clients = useSelector((state) => state.client.clients);

    useEffect(() => {
        dispatch(getDatevConnections());
    }, [dispatch]);

    const user = useSelector((state) => state.authentication.user);
    const [showAutoUpdateModal, setShowAutoUpdateModal] = useState(false);

    const isSuperUser = useMemo(
        () => user && (user.userRole === UserRole.SUPER_USER || user.userRole === UserRole.TECHNICAL_USER),
        [user]
    );

    const getStripeDashboardUrl = (isSuperUser, customerStripeEndpoint) => {
        const pathwayStripeEndpoint = process.env.REACT_APP_PATHWAY_STRIPE_CONNECTION_ENDPOINT;

        if (isSuperUser && pathwayStripeEndpoint) {
            return `https://dashboard.stripe.com/${pathwayStripeEndpoint}/connect/view-as/${customerStripeEndpoint}/balance/overview`;
        }

        return `https://dashboard.stripe.com/${customerStripeEndpoint}`;
    };

    return (
        <>
            <ClientAutoUpdateFromProdModal
                showModal={getEnvironment() === EnvironmentEnum.DEV && showAutoUpdateModal}
                closeModal={() => setShowAutoUpdateModal(false)}
            />
            <PathwayTable>
                <TopTableContent>
                    <WarningMessage marginless messages={clientReducer.datevConnectionsWarningMessage} />
                </TopTableContent>
                <TableHeader>
                    <PathwayTableHeader>Name</PathwayTableHeader>
                    <PathwayTableHeader>Notiz</PathwayTableHeader>
                    <PathwayTableHeader></PathwayTableHeader>
                    <PathwayCenteredTableHeader>API Verbindungen</PathwayCenteredTableHeader>
                    <PathwayCenteredTableHeader>DATEV</PathwayCenteredTableHeader>
                    <PathwayTableHeader>
                        <ButtonLayout>
                            {getEnvironment() === EnvironmentEnum.DEV && isSuperUser && (
                                <PathwayButton
                                    color="warning"
                                    onClick={() => setShowAutoUpdateModal(true)}
                                    dataCy="prod-sync-button"
                                >
                                    Prod Sync
                                </PathwayButton>
                            )}
                            <DatevConnectionButton
                                clients={clients}
                                isOpen={props.showDatevConnectionModal}
                                onClose={() => props.setShowDatevConnectionModal(false)}
                                dataCy="datev-connection-modal-button"
                            />
                            <PathwayAddButton
                                onClick={props.createNewClient}
                                dataCy={"client-add-button"}
                                iconSize="2rem"
                            />
                        </ButtonLayout>
                    </PathwayTableHeader>
                </TableHeader>

                {clients.map((client, index1) => (
                    <React.Fragment key={client.externalUUID}>
                        <TableRow>
                            <PathwayTableCellContent style={{ wordBreak: "break-word" }}>
                                {client.name}
                            </PathwayTableCellContent>
                            <PathwayTableCellContent>{client.note}</PathwayTableCellContent>
                            {client.connectedTo.some((c) => c.type === "STRIPE_CONNECT") ? (
                                <ButtonCell>
                                    <PathwayButton
                                        color="primary"
                                        dataCy={`stripe-connect-button-${index1}`}
                                        onClick={() => {
                                            const stripeConnection = client.connectedTo.find(
                                                (c) => c.type === "STRIPE_CONNECT"
                                            );

                                            if (stripeConnection) {
                                                const url = getStripeDashboardUrl(
                                                    isSuperUser,
                                                    stripeConnection.endpoint
                                                );
                                                window.open(url, "_blank");
                                            }
                                        }}
                                    >
                                        Zum Stripe Connect Account
                                    </PathwayButton>
                                </ButtonCell>
                            ) : (
                                <ButtonCell></ButtonCell>
                            )}
                            <ButtonCell>
                                <PathwayButton
                                    dataCy={"add-connection-button-" + index1}
                                    color="primary"
                                    onClick={() => props.createNewConnectedAuthDataForClient(client)}
                                >
                                    Verbindung hinzufügen
                                </PathwayButton>
                                {client.connectedTo.map((connection, index2) => (
                                    <PathwayButton
                                        key={connection.externalUUID}
                                        color="primary"
                                        dataCy={
                                            stringUtils.formatUppercaseIdentifierToLowercase(connection.type) +
                                            "-connection-button-" +
                                            index1 +
                                            "-" +
                                            index2
                                        }
                                        onClick={() => props.editExistingConnectedAuthData(connection)}
                                    >
                                        <AuthDataButtonLayout>
                                            <p>{connection.title}</p>
                                            {connection.status === AuthDataStatus.REVOKED && "(Deinstalliert)"}{" "}
                                            {connection.status === AuthDataStatus.SETUP &&
                                                (!connection.createdAt ||
                                                    dateUtils.getDurationBetweenInMs(
                                                        new Date(connection.createdAt),
                                                        new Date()
                                                    ) <
                                                        1000 * 60 * 10) && (
                                                    <LoadingSpinner size="1.6rem" loading={true} primary={false} />
                                                )}
                                            {connection.status === AuthDataStatus.SETUP &&
                                                connection.type === AuthDataType.PAYPAL.identifier &&
                                                connection.createdAt &&
                                                dateUtils.getDurationBetweenInMs(
                                                    new Date(connection.createdAt),
                                                    new Date()
                                                ) >=
                                                    1000 * 60 * 10 && <p>(bitte erneut versuchen)</p>}
                                        </AuthDataButtonLayout>
                                    </PathwayButton>
                                ))}
                            </ButtonCell>
                            <PathwayCenteredTableCellContent>
                                <DatevRow data-cy="datev-row">
                                    <Paragraph>Mandat:</Paragraph>
                                    <Paragraph>{client.clientNumber}</Paragraph>
                                </DatevRow>
                                <DatevRow>
                                    <Paragraph>Berater:</Paragraph>
                                    <Paragraph>{client.consultantNumber}</Paragraph>
                                </DatevRow>
                            </PathwayCenteredTableCellContent>
                            <PathwayCenteredTableCellContent>
                                <DatevRow>
                                    <PathwayEditButton
                                        onClick={() => props.openEditClientModal(client)}
                                        dataCy={"client-edit-button-" + index1}
                                    />
                                    <PathwayDeleteButton
                                        onClick={() => props.openDeleteClientModal(client)}
                                        dataCy={"client-delete-button-" + index1}
                                    />
                                </DatevRow>
                            </PathwayCenteredTableCellContent>
                        </TableRow>
                        <TableContentDividerLine />
                    </React.Fragment>
                ))}
            </PathwayTable>
        </>
    );
};

export default ClientsScreenClientsTabContent;

const AuthDataButtonLayout = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, max-content));
    grid-template-rows: 1fr;
    column-gap: var(--gap-s);
    justify-content: center;

    & p {
        margin: 0;
    }
`;

const TableHeader = styled(PathwayTableHeaderRow)`
    grid-template-columns: 0.2fr 0.2fr 0.1fr 0.25fr 0.11fr 0.09fr;
`;

const TableRow = styled(PathwayTableContentRow)`
    grid-template-columns: 0.2fr 0.2fr 0.1fr 0.25fr 0.11fr 0.09fr;
`;

const ButtonCell = styled(PathwayCenteredTableCellContent)`
    row-gap: var(--gap-s);
`;

const DatevRow = styled.div`
    display: grid;
    grid-template-columns: 0.5fr 0.5fr;
    column-gap: var(--gap-s);
`;

const TopTableContent = styled.div`
    display: grid;
    grid-template-columns: auto max-content;
    column-gap: var(--gap-l);
    align-items: end;
`;

const ButtonLayout = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(50px, max-content));
    justify-content: end;
    column-gap: var(--gap-l);
    align-items: center;
`;
